// Shared subject-collection primitives (2026-08-25).
//
// One canonical way to present grade → subject mappings across the app,
// replacing per-page chip walls that made class/staff pages hundreds of
// pixels taller for broad multi-grade classes. Two exports:
//
//   GradeSubjectsSummary   Compact card. Renders inline when the
//                          collection is small; otherwise renders
//                          counts + preview + "View All" button that
//                          opens the shared modal.
//
//   SubjectDetailsDialog   The premium modal. Header (icon tile,
//                          title, counts). Optional context strip
//                          (teacher / location / etc). Optional
//                          per-grade "+N more" expand. Optional
//                          frontend-only search. Optional footer
//                          action button. Escape closes; MUI Dialog
//                          traps focus for a11y.
//
// Data contract is the same `grade_subjects` DTO returned by every
// backend endpoint that already emits it (dashboard/operations,
// class-schedules details, attendance-sessions report, etc):
//
//   [{ grade_id, grade_name, subjects: [{id, name}] }]
//
// Legacy shapes accepted by the underlying GradeSubjectGroups
// component are also normalized here (grade as string, subjects as
// plain strings) so the shared primitive can be introduced without
// forcing every caller to reshape their payload.

import React, { useMemo, useState } from "react";
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent,
  IconButton, TextField, InputAdornment, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubjectItem { id?: number; name: string; sort_order?: number | null; }
export interface GradeGroup {
  grade_id?: number;
  grade_name?: string;
  grade?: string | { id?: number; name?: string; sort_order?: number | null };
  subjects: Array<SubjectItem | string>;
  sort_order?: number | null;
}

interface Counts { grades: number; uniqueSubjects: number; mappings: number; }

// ---------------------------------------------------------------------------
// Normalization + counts. Preserves grade × subject relationships
// exactly (spec §20). "unique subjects" counts distinct subject ids
// across all grades; "mappings" counts every grade→subject pair.
// ---------------------------------------------------------------------------

const gradeLabel = (g: GradeGroup): string => {
  if (g.grade_name) return g.grade_name;
  if (typeof g.grade === "string") return g.grade;
  return g.grade?.name || "";
};
const subjectName = (s: SubjectItem | string): string =>
  typeof s === "string" ? s : (s?.name || "");
const subjectId = (s: SubjectItem | string): string | null => {
  if (typeof s === "string") return `n:${s.trim().toLowerCase()}`;
  if (s?.id != null) return `i:${s.id}`;
  return s?.name ? `n:${s.name.trim().toLowerCase()}` : null;
};

// Deduplicate subjects within a grade (same id or normalized name).
const dedupSubjects = (subs: Array<SubjectItem | string>): Array<SubjectItem | string> => {
  const seen = new Set<string>();
  const out: Array<SubjectItem | string> = [];
  for (const s of subs) {
    const name = subjectName(s).trim();
    if (!name) continue;
    const key = subjectId(s);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

const CANONICAL_GRADE_ORDER = [
  "pre-k", "prek", "pre k", "jk", "junior kindergarten",
  "sk", "senior kindergarten",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "adult",
];
const gradeRank = (name: string): number => {
  const n = name.trim().toLowerCase();
  const stripped = n.replace(/^grade\s*/, "");
  const direct = CANONICAL_GRADE_ORDER.indexOf(n);
  if (direct >= 0) return direct;
  const bare = CANONICAL_GRADE_ORDER.indexOf(stripped);
  return bare >= 0 ? bare : 999;
};

const cleanGroups = (groups?: GradeGroup[] | null): GradeGroup[] => {
  const src = Array.isArray(groups) ? groups : [];
  const shaped = src
    .map((g) => ({ ...g, subjects: dedupSubjects(g.subjects || []) }))
    .filter((g) => gradeLabel(g));
  shaped.sort((a, b) => gradeRank(gradeLabel(a)) - gradeRank(gradeLabel(b)));
  return shaped;
};

const countsFor = (groups: GradeGroup[]): Counts => {
  const unique = new Set<string>();
  let mappings = 0;
  for (const g of groups) {
    for (const s of g.subjects) {
      const k = subjectId(s);
      if (!k) continue;
      unique.add(k);
      mappings += 1;
    }
  }
  return { grades: groups.length, uniqueSubjects: unique.size, mappings };
};

const displayGradeLabel = (raw: string): string =>
  /^(pre-?k|jk|sk|adult|\d+)$/i.test(raw.trim()) ? `Grade ${raw}` : raw;

// ---------------------------------------------------------------------------
// Palette — muted premium (matches the current dashboard/parent portal).
// ---------------------------------------------------------------------------

const palette = {
  navy:      "#17324D",
  blue:      "#4E78A6",
  steel:     "#6F96BE",
  muted:     "#7D8FA1",
  surface:   "#FFFFFF",
  surfaceBlue: "#EAF2F8",
  surfaceGrey: "#F3F5F8",
  border:    "#E2E8F0",
  borderSoft:"#DFE7EE",
};

// ---------------------------------------------------------------------------
// SubjectDetailsDialog — the shared modal. Grade-grouped list with an
// optional context strip, optional search, and optional footer action.
// ---------------------------------------------------------------------------

export interface SubjectDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  groups?: GradeGroup[] | null;
  title?: string;
  subtitle?: string;
  /** Optional grid of "TEACHER / LOCATION / etc" chips. */
  contextStrip?: React.ReactNode;
  /** Optional footer action button (e.g. "Open Attendance"). */
  footerAction?: React.ReactNode;
  /** Show the frontend search input. Auto-enables at >= 6 grades. */
  enableSearch?: boolean;
  /** Collapse each grade's subjects when it has more than this many. */
  subjectsPerGradePreview?: number;
}

export const SubjectDetailsDialog: React.FC<SubjectDetailsDialogProps> = ({
  open, onClose,
  groups, title = "Grades & Subjects",
  subtitle = "Review all subject mappings for this class.",
  contextStrip, footerAction,
  enableSearch,
  subjectsPerGradePreview = 8,
}) => {
  const clean = useMemo(() => cleanGroups(groups), [groups]);
  const counts = useMemo(() => countsFor(clean), [clean]);
  const showSearch = enableSearch ?? clean.length >= 6;
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clean;
    return clean
      .map((g) => {
        const gname = gradeLabel(g).toLowerCase();
        if (gname.includes(q)) return g; // whole grade matches → keep all subjects
        return { ...g, subjects: g.subjects.filter((s) => subjectName(s).toLowerCase().includes(q)) };
      })
      .filter((g) => g.subjects.length > 0);
  }, [clean, query]);

  const toggleExpand = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md" fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "18px" },
          maxHeight: { xs: "100%", sm: "85vh" },
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          height: { xs: "100%", sm: "auto" },
          border: `1px solid ${palette.border}`,
          boxShadow: "0 12px 40px rgba(23,50,77,0.14)",
        },
      }}
      aria-labelledby="subject-details-title"
    >
      <DialogTitle
        id="subject-details-title"
        sx={{ px: 3, py: 2, borderBottom: `1px solid ${palette.border}` }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: "12px",
            bgcolor: palette.surfaceBlue, color: palette.blue,
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { fontSize: 22 }, flexShrink: 0,
          }}>
            <MenuBookOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: palette.navy, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: palette.muted, mt: 0.25 }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Chip size="small" label={`${counts.grades} Grade${counts.grades === 1 ? "" : "s"}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceBlue, color: palette.blue, border: `1px solid ${palette.border}` }} />
            <Chip size="small" label={`${counts.uniqueSubjects} Subject${counts.uniqueSubjects === 1 ? "" : "s"}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceGrey, color: palette.navy, border: `1px solid ${palette.border}` }} />
            <Chip size="small" label={`${counts.mappings} Mapping${counts.mappings === 1 ? "" : "s"}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceGrey, color: palette.muted, border: `1px solid ${palette.border}` }} />
            <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ ml: 0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {contextStrip && (
          <Box sx={{ mb: 2 }}>{contextStrip}</Box>
        )}

        {showSearch && (
          <TextField
            fullWidth size="small" autoFocus={false}
            placeholder="Search grade or subject…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: palette.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", fontSize: 13,
                "& fieldset": { borderColor: palette.border },
                "&:hover fieldset": { borderColor: palette.steel },
                "&.Mui-focused fieldset": { borderColor: palette.blue, borderWidth: "2px" },
              },
            }}
          />
        )}

        {filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: palette.navy, mb: 0.5 }}>
              {query ? "No matches" : "No grade-subject mappings"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: palette.muted }}>
              {query ? "Try a different grade or subject." : "This class has no linked grades or subjects."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {filtered.map((g, i) => {
              const key = String(g.grade_id ?? gradeLabel(g) ?? i);
              const label = displayGradeLabel(gradeLabel(g));
              const total = g.subjects.length;
              const shouldCollapse = total > subjectsPerGradePreview && !expanded.has(key);
              const shown = shouldCollapse ? g.subjects.slice(0, subjectsPerGradePreview) : g.subjects;
              const overflow = total - shown.length;

              return (
                <Box key={key} sx={{
                  p: 1.5, borderRadius: "12px",
                  bgcolor: palette.surface, border: `1px solid ${palette.border}`,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: shown.length ? 1 : 0 }}>
                    <Box sx={{
                      minWidth: 84, px: 1, py: 0.5,
                      bgcolor: palette.surfaceBlue, color: palette.blue,
                      border: `1px solid ${palette.border}`,
                      borderRadius: "8px",
                      textAlign: "center", flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{label}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: palette.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {total} subject{total === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {shown.length === 0 ? (
                      <Typography sx={{ fontSize: 11.5, color: palette.muted, fontStyle: "italic" }}>
                        No subjects mapped
                      </Typography>
                    ) : shown.map((s, si) => {
                      const name = subjectName(s);
                      return (
                        <Chip key={`${key}-${si}-${name}`} label={name} size="small"
                          sx={{
                            height: 24, fontSize: 11.5, fontWeight: 700,
                            bgcolor: palette.surfaceGrey, color: palette.navy,
                            border: `1px solid ${palette.border}`,
                          }} />
                      );
                    })}
                    {overflow > 0 && (
                      <Chip
                        label={`+${overflow} more`} size="small" clickable
                        onClick={() => toggleExpand(key)}
                        sx={{
                          height: 24, fontSize: 11.5, fontWeight: 800,
                          bgcolor: palette.surfaceBlue, color: palette.blue,
                          border: `1px solid ${palette.border}`,
                          cursor: "pointer",
                          "&:hover": { bgcolor: alpha(palette.blue, 0.12) },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {(footerAction || true) && (
        <Box sx={{
          px: 3, py: 2, borderTop: `1px solid ${palette.border}`,
          display: "flex", justifyContent: "flex-end", gap: 1,
        }}>
          <Button onClick={onClose} sx={{
            textTransform: "none", fontWeight: 700, borderRadius: 2,
            color: palette.navy,
          }}>Close</Button>
          {footerAction}
        </Box>
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// GradeSubjectsSummary — compact card that auto-adapts.
//
// Small collections (<= inlineThreshold mappings AND <= 3 grades) render
// as a compact preview strip with no modal, matching spec §3.
// Large collections render counts + preview + "View All Grades &
// Subjects" button that opens SubjectDetailsDialog.
//
// The primitive owns its own dialog state so callers don't have to
// wire it up — dropping it in place of an inline chip wall is a
// one-line change.
// ---------------------------------------------------------------------------

export interface GradeSubjectsSummaryProps {
  groups?: GradeGroup[] | null;
  /** Card title in the modal + optional aria-label. */
  modalTitle?: string;
  /** Optional context strip rendered above the grade list in the modal. */
  modalContextStrip?: React.ReactNode;
  /** Extra footer action button (e.g. "Open Attendance"). */
  modalFooterAction?: React.ReactNode;
  /** Override the threshold at which a modal is used. Default: 3 grades. */
  inlineGradeLimit?: number;
  /** Override the total-mapping threshold. Default: 8. */
  inlineMappingLimit?: number;
  /** Subjects to preview per grade in the compact card. Default: 3. */
  previewSubjectsPerGrade?: number;
  /** Grades to preview in the compact card. Default: 3. */
  previewGradeLimit?: number;
  /** Prop passthrough for the modal search override. */
  enableSearch?: boolean;
  /**
   * `"card"` (default) renders the counts + preview + button.
   * `"hero"` renders just the counts row + button, no preview grid —
   * for use inside a class hero where vertical space is precious.
   */
  variant?: "card" | "hero";
  /** Custom label for the View button (default depends on variant). */
  viewButtonLabel?: string;
}

export const GradeSubjectsSummary: React.FC<GradeSubjectsSummaryProps> = ({
  groups,
  modalTitle = "Grades & Subjects",
  modalContextStrip,
  modalFooterAction,
  inlineGradeLimit = 3,
  inlineMappingLimit = 8,
  previewSubjectsPerGrade = 3,
  previewGradeLimit = 3,
  enableSearch,
  variant = "card",
  viewButtonLabel,
}) => {
  const [open, setOpen] = useState(false);
  const clean = useMemo(() => cleanGroups(groups), [groups]);
  const counts = useMemo(() => countsFor(clean), [clean]);

  if (clean.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: palette.muted, fontStyle: "italic" }}>
        No grade-specific subjects configured.
      </Typography>
    );
  }

  const isSmall = clean.length <= inlineGradeLimit && counts.mappings <= inlineMappingLimit;

  // Hero variant → always render the compact "N Grades · N Subjects
  // [View Grades & Subjects]" line, no preview grid, no inline list —
  // reserves the hero for the identity line, not the whole matrix.
  if (variant === "hero") {
    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.navy }}>
            {counts.grades} Grade{counts.grades === 1 ? "" : "s"}
            <Box component="span" sx={{ color: palette.muted, mx: 0.75, fontWeight: 500 }}>·</Box>
            {counts.uniqueSubjects} Subject{counts.uniqueSubjects === 1 ? "" : "s"}
            {counts.mappings !== counts.uniqueSubjects && (
              <>
                <Box component="span" sx={{ color: palette.muted, mx: 0.75, fontWeight: 500 }}>·</Box>
                <Box component="span" sx={{ color: palette.muted, fontWeight: 700 }}>
                  {counts.mappings} Mapping{counts.mappings === 1 ? "" : "s"}
                </Box>
              </>
            )}
          </Typography>
          <Button
            size="small" variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "15px !important" }} />}
            onClick={() => setOpen(true)}
            sx={{
              textTransform: "none", fontWeight: 800, borderRadius: 2,
              borderColor: palette.blue, color: palette.blue, py: 0.25, px: 1.25,
              minHeight: 28,
              "&:hover": { borderColor: palette.blue, bgcolor: alpha(palette.blue, 0.06) },
            }}
          >
            {viewButtonLabel || "View Grades & Subjects"}
          </Button>
        </Box>
        <SubjectDetailsDialog
          open={open} onClose={() => setOpen(false)}
          groups={clean} title={modalTitle}
          contextStrip={modalContextStrip}
          footerAction={modalFooterAction}
          enableSearch={enableSearch}
        />
      </>
    );
  }

  // Small collection → inline compact list, no modal.
  if (isSmall) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {clean.map((g, i) => {
          const key = String(g.grade_id ?? gradeLabel(g) ?? i);
          const label = displayGradeLabel(gradeLabel(g));
          return (
            <Box key={key} sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
              <Typography component="span" sx={{
                fontSize: 12.5, fontWeight: 800, color: palette.blue,
                minWidth: 62, flexShrink: 0, whiteSpace: "nowrap",
              }}>
                {label}
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, color: palette.navy, lineHeight: 1.4 }}>
                {g.subjects.map((s) => subjectName(s)).join(", ")}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Large collection → summary + preview + View All button.
  const previewGrades = clean.slice(0, previewGradeLimit);
  const previewOverflow = clean.length - previewGrades.length;

  return (
    <>
      <Box>
        {/* Counts row */}
        <Box sx={{
          display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25,
        }}>
          <Chip size="small" label={`${counts.grades} grades`}
            sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceBlue, color: palette.blue, border: `1px solid ${palette.border}` }} />
          <Chip size="small" label={`${counts.uniqueSubjects} unique subjects`}
            sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceGrey, color: palette.navy, border: `1px solid ${palette.border}` }} />
          <Chip size="small" label={`${counts.mappings} mappings`}
            sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: palette.surfaceGrey, color: palette.muted, border: `1px solid ${palette.border}` }} />
        </Box>

        {/* Preview: first N grades, first M subjects each */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
          {previewGrades.map((g, i) => {
            const key = String(g.grade_id ?? gradeLabel(g) ?? i);
            const label = displayGradeLabel(gradeLabel(g));
            const names = g.subjects.slice(0, previewSubjectsPerGrade).map((s) => subjectName(s));
            const rest = g.subjects.length - names.length;
            return (
              <Box key={key} sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                <Typography component="span" sx={{
                  fontSize: 12, fontWeight: 800, color: palette.blue,
                  minWidth: 62, flexShrink: 0, whiteSpace: "nowrap",
                }}>
                  {label}
                </Typography>
                <Typography component="span" sx={{
                  fontSize: 12.5, color: palette.navy, lineHeight: 1.4,
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {names.join(" · ")}
                  {rest > 0 && (
                    <Box component="span" sx={{ color: palette.muted, fontWeight: 700, ml: 0.5 }}>
                      +{rest}
                    </Box>
                  )}
                </Typography>
              </Box>
            );
          })}
          {previewOverflow > 0 && (
            <Typography sx={{ fontSize: 11.5, color: palette.muted, fontWeight: 700, mt: 0.25 }}>
              +{previewOverflow} more grade{previewOverflow === 1 ? "" : "s"}
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined" size="small"
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: "none", fontWeight: 800, borderRadius: 2,
            borderColor: palette.blue, color: palette.blue,
            "&:hover": { borderColor: palette.blue, bgcolor: alpha(palette.blue, 0.06) },
          }}
        >
          {viewButtonLabel || "View All Grades & Subjects"}
        </Button>
      </Box>

      <SubjectDetailsDialog
        open={open} onClose={() => setOpen(false)}
        groups={clean} title={modalTitle}
        contextStrip={modalContextStrip}
        footerAction={modalFooterAction}
        enableSearch={enableSearch}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// FlatSubjectsSummary — companion primitive for callers that only have a
// flat list of subject names (Staff Details, Student profile snippets,
// enrolled subjects), not a grade → subject matrix. Reuses the same
// SubjectDetailsDialog by wrapping the list in a single synthetic grade
// group so the modal renders one clean panel with search + chip wrap.
//
// Rules per spec §7:
//   count ≤ inlineLimit → render inline chips, no button
//   count >  inlineLimit → render first `previewLimit` chips + "+N more"
//                          + "View Subjects" button that opens the modal
// ---------------------------------------------------------------------------

export interface FlatSubjectsSummaryProps {
  subjects?: Array<string | SubjectItem> | null;
  modalTitle?: string;
  modalSubtitle?: string;
  /** Threshold below which the list stays inline. Default: 4. */
  inlineLimit?: number;
  /** Chip preview count when the list overflows. Default: 3. */
  previewLimit?: number;
  /** Empty-state label. Default: "—" */
  emptyLabel?: string;
  /** Custom View button label. Default: "View Subjects". */
  viewButtonLabel?: string;
}

export const FlatSubjectsSummary: React.FC<FlatSubjectsSummaryProps> = ({
  subjects,
  modalTitle = "Subjects",
  modalSubtitle = "Full list of subjects.",
  inlineLimit = 4,
  previewLimit = 3,
  emptyLabel = "—",
  viewButtonLabel,
}) => {
  const [open, setOpen] = useState(false);
  const clean = useMemo(() => {
    const src = Array.isArray(subjects) ? subjects : [];
    // Dedup by id / normalized name; preserve order.
    const seen = new Set<string>();
    const out: Array<string | SubjectItem> = [];
    for (const s of src) {
      const name = subjectName(s).trim();
      if (!name) continue;
      const key = subjectId(s);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
  }, [subjects]);

  if (clean.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: palette.muted }}>{emptyLabel}</Typography>
    );
  }

  // Single synthetic grade group so the shared modal can render the
  // list without any special-case rendering path.
  const modalGroups: GradeGroup[] = [
    { grade_id: 0, grade_name: "Subjects", subjects: clean.map((s) => (typeof s === "string" ? { name: s } : s)) },
  ];

  // Small list → render every chip inline. No button.
  if (clean.length <= inlineLimit) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {clean.map((s, i) => (
          <Chip key={`${i}-${subjectName(s)}`} label={subjectName(s)} size="small"
            sx={{
              height: 22, fontSize: 11, fontWeight: 700,
              bgcolor: palette.surfaceBlue, color: palette.navy,
              border: `1px solid ${palette.border}`,
            }} />
        ))}
      </Box>
    );
  }

  // Large list → count + preview chips + "+N more" + View Subjects.
  const previewChips = clean.slice(0, previewLimit);
  const overflow = clean.length - previewChips.length;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: palette.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {clean.length} subject{clean.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {previewChips.map((s, i) => (
            <Chip key={`${i}-${subjectName(s)}`} label={subjectName(s)} size="small"
              sx={{
                height: 22, fontSize: 11, fontWeight: 700,
                bgcolor: palette.surfaceBlue, color: palette.navy,
                border: `1px solid ${palette.border}`,
              }} />
          ))}
          {overflow > 0 && (
            <Chip
              label={`+${overflow} more`} size="small" clickable
              onClick={() => setOpen(true)}
              sx={{
                height: 22, fontSize: 11, fontWeight: 800,
                bgcolor: palette.surfaceGrey, color: palette.blue,
                border: `1px solid ${palette.border}`,
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(palette.blue, 0.10) },
              }}
            />
          )}
        </Box>
        <Button
          size="small" variant="text"
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "15px !important" }} />}
          onClick={() => setOpen(true)}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none", fontWeight: 800, borderRadius: 2,
            color: palette.blue, mt: 0.25, px: 1, py: 0.25, minHeight: 26,
            "&:hover": { bgcolor: alpha(palette.blue, 0.06) },
          }}
        >
          {viewButtonLabel || "View Subjects"}
        </Button>
      </Box>

      <SubjectDetailsDialog
        open={open} onClose={() => setOpen(false)}
        groups={modalGroups} title={modalTitle} subtitle={modalSubtitle}
        subjectsPerGradePreview={999}
      />
    </>
  );
};

export default GradeSubjectsSummary;
